import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ExternalLink, ShieldCheck, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

export default function AdBanner({ placement = "parent_corner_header" }) {
  const navigate = useNavigate();
  const [ad, setAd] = useState(null);
  const [loading, setLoading] = useState(true);
  const trackedImpression = useRef(false);

  useEffect(() => {
    let active = true;
    async function loadAd() {
      try {
        const adsList = await api.getActiveAds(placement);
        const promoAd = {
          id: "promo_placeholder",
          advertiser_name: "Pasang Produk ANDA disini...",
          image_url: "promo_placeholder",
          target_url: "/partner",
          is_promo: true
        };

        let combinedAds = [];
        if (adsList && adsList.length > 0) {
          combinedAds = [...adsList, promoAd];
        } else {
          combinedAds = [promoAd];
        }

        if (active && combinedAds.length > 0) {
          const randomIndex = Math.floor(Math.random() * combinedAds.length);
          setAd(combinedAds[randomIndex]);
        }
      } catch (err) {
        console.error("Gagal memuat banner kemitraan:", err);
        if (active) {
          setAd({
            id: "promo_placeholder",
            advertiser_name: "Pasang Produk ANDA disini...",
            image_url: "promo_placeholder",
            target_url: "/partner",
            is_promo: true
          });
        }
      } finally {
        if (active) setLoading(false);
      }
    }
    loadAd();
    return () => {
      active = false;
    };
  }, [placement]);

  // Track impression once the ad is loaded and displayed
  useEffect(() => {
    if (ad && ad.id !== "promo_placeholder" && !trackedImpression.current) {
      trackedImpression.current = true;
      api.trackAdEvent({
        campaign_id: ad.id,
        event_type: "impression"
      }).catch(err => console.error("Gagal mencatat impresi iklan:", err));
    }
  }, [ad]);

  const handleAdClick = async () => {
    if (!ad) return;
    if (ad.id === "promo_placeholder") {
      navigate("/partner");
      return;
    }
    try {
      await api.trackAdEvent({
        campaign_id: ad.id,
        event_type: "click"
      });
    } catch (err) {
      console.error("Gagal mencatat klik iklan:", err);
    }
    // Open target url in a new window/tab safely
    window.open(ad.target_url, "_blank", "noopener,noreferrer");
  };

  if (loading || !ad) return null;

  if (ad.id === "promo_placeholder") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
        onClick={handleAdClick}
        className="w-full relative group cursor-pointer overflow-hidden rounded-[2rem] bg-gradient-to-r from-slate-900/60 to-amber-950/20 hover:from-slate-900/80 hover:to-amber-950/40 border border-amber-500/30 hover:border-amber-400/50 transition-all p-4 flex flex-col sm:flex-row items-center gap-4 shadow-xl select-none"
      >
        {/* Glossy Backdrop effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-amber-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

        {/* Ad Image banner placeholder container */}
        <div className="w-full sm:w-48 h-28 rounded-2xl bg-amber-500/10 border border-amber-500/20 overflow-hidden flex-shrink-0 relative flex flex-col items-center justify-center text-center p-2">
          <Sparkles className="w-8 h-8 text-amber-400 animate-pulse mb-1" />
          <span className="text-[10px] font-extrabold text-amber-300 uppercase tracking-widest leading-tight">
            Spot Promosi
          </span>
        </div>

        {/* Ad content details */}
        <div className="flex-1 flex flex-col justify-between self-stretch py-1">
          <div>
            <div className="flex items-center gap-1.5 mb-1.5">
              <span className="text-[10px] font-extrabold tracking-wider text-slate-400 uppercase">
                Peluang Kemitraan
              </span>
              <div className="w-1 h-1 rounded-full bg-amber-500" />
              <span className="text-[10px] font-extrabold tracking-wider text-amber-400 uppercase">
                Sponsor Baru
              </span>
            </div>
            <h4 className="font-display font-bold text-lg text-slate-100 group-hover:text-amber-400 transition-colors leading-snug">
              Pasang Produk ANDA disini...
            </h4>
            <p className="text-xs text-slate-400 font-bold mt-1 line-clamp-2 leading-relaxed">
              Jangkau ribuan orang tua aktif yang peduli pada tumbuh kembang anak secara langsung di aplikasi Petualangan Bayi. Klik di sini untuk memesan slot iklan instan Anda.
            </p>
          </div>

          <div className="flex items-center gap-1 text-[11px] font-extrabold text-amber-400 mt-2 sm:mt-0 group-hover:translate-x-1 transition-transform">
            <span>Mulai Pasang Iklan</span>
            <ExternalLink className="w-3.5 h-3.5" />
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      onClick={handleAdClick}
      className="w-full relative group cursor-pointer overflow-hidden rounded-[2rem] bg-slate-900/40 hover:bg-slate-900/60 border border-slate-800/80 hover:border-slate-700/80 transition-all p-4 flex flex-col sm:flex-row items-center gap-4 shadow-xl select-none"
    >
      {/* Glossy Backdrop effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-amber-500/5 to-sky-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

      {/* Ad Image banner container */}
      <div className="w-full sm:w-48 h-28 rounded-2xl bg-slate-950 border border-slate-850 overflow-hidden flex-shrink-0 relative">
        <img
          src={ad.image_url}
          alt={ad.advertiser_name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={(e) => {
            e.target.src = "https://placehold.co/300x150/1e293b/cbd5e1?text=Kemitraan+BabyLabs";
          }}
        />
        {/* Curated sponsor badge overlay */}
        <div className="absolute top-2 left-2 px-2.5 py-0.5 rounded-full bg-slate-950/80 backdrop-blur-md border border-slate-800/60 flex items-center gap-1">
          <ShieldCheck className="w-3 h-3 text-emerald-400 fill-emerald-400/10" />
          <span className="text-[9px] font-extrabold tracking-wider text-slate-300 uppercase">
            Terkurasi
          </span>
        </div>
      </div>

      {/* Ad content details */}
      <div className="flex-1 flex flex-col justify-between self-stretch py-1">
        <div>
          <div className="flex items-center gap-1.5 mb-1.5">
            <span className="text-[10px] font-extrabold tracking-wider text-slate-400 uppercase">
              Rekomendasi Partner Orang Tua
            </span>
            <div className="w-1 h-1 rounded-full bg-slate-600" />
            <span className="text-[10px] font-extrabold tracking-wider text-amber-400 uppercase">
              Sponsor
            </span>
          </div>
          <h4 className="font-display font-bold text-base text-slate-100 group-hover:text-amber-400 transition-colors leading-snug">
            {ad.advertiser_name}
          </h4>
          <p className="text-xs text-slate-400 font-bold mt-1 line-clamp-2">
            Klik di sini untuk melihat penawaran eksklusif serta rekomendasi terbaik bagi buah hati Anda langsung dari partner tepercaya kami.
          </p>
        </div>

        <div className="flex items-center gap-1 text-[11px] font-extrabold text-sky-400 mt-2 sm:mt-0 group-hover:translate-x-1 transition-transform">
          <span>Kunjungi Website Resmi</span>
          <ExternalLink className="w-3.5 h-3.5" />
        </div>
      </div>
    </motion.div>
  );
}
